import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import BackButton from '../../components/common/BackButton';
import userApi, { User } from '../../services/api/user.api';
import applicationApi from '../../services/api/application.api';
import roleApi, { Role } from '../../services/api/role.api';

export default function UsersPage() {
  const { t } = useTranslation(['settings', 'common']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleIds, setInviteRoleIds] = useState<string[]>([]);
  const [inviteApplicationIds, setInviteApplicationIds] = useState<string[]>([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    status: 'active' as 'active' | 'inactive' | 'suspended',
    roleIds: [] as string[],
    applicationIds: [] as string[],
  });

  useEffect(() => {
    fetchUsers();
    fetchApplications();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userApi.getAll();
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
      console.error('Users fetch error:', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await applicationApi.getAll();
      setApplications(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      console.error('Applications fetch error:', err);
      setApplications([]);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await roleApi.getAll();
      console.log('Roles API response:', response);

      // Check if response has a data property (standard API response format)
      const rolesData = response.data || response;
      console.log('Roles data:', rolesData);

      setRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (err: any) {
      console.error('Roles fetch error:', err);
      setRoles([]);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      case 'developer':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'viewer':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
      case 'suspended':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'invited':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const handleInvite = async () => {
    if (!inviteFirstName || !inviteLastName || !inviteEmail) {
      toast.error(t('common:validation.required'));
      return;
    }

    const loadingToast = toast.loading(t('settings:users.sendingInvite') || 'Sending invitation...');

    try {
      const response = await userApi.invite({
        firstName: inviteFirstName,
        lastName: inviteLastName,
        email: inviteEmail,
        roleIds: inviteRoleIds
      });

      // Assign applications if any were selected
      if (inviteApplicationIds.length > 0 && response.data?.id) {
        await userApi.assignApplications(response.data.id, inviteApplicationIds);
      }

      toast.success(t('settings:users.inviteSuccess') || 'Invitation sent successfully!', { id: loadingToast });

      setShowInviteModal(false);
      setInviteFirstName('');
      setInviteLastName('');
      setInviteEmail('');
      setInviteRoleIds([]);
      setInviteApplicationIds([]);
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      toast.error(err.message || t('settings:users.inviteError') || 'Failed to send invitation', { id: loadingToast });
    }
  };

  const handleResendInvite = async (userId: string, userEmail: string) => {
    const loadingToast = toast.loading(t('settings:users.resendingInvite') || 'Resending invitation...');

    try {
      await userApi.resendInvite(userId);
      toast.success(t('settings:users.resendSuccess') || `Invitation email resent to ${userEmail}`, { id: loadingToast });
    } catch (err: any) {
      toast.error(err.message || t('settings:users.resendError') || 'Failed to resend invitation', { id: loadingToast });
    }
  };

  const handleEdit = async (user: User) => {
    setEditUser(user);

    // Load user's applications
    try {
      const appsResponse = await userApi.getUserApplications(user.id);
      const userAppIds = (appsResponse.data || []).map((app: any) => app.id);

      setEditFormData({
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        status: user.status,
        roleIds: user.roles.map((r) => r.id),
        applicationIds: userAppIds,
      });
    } catch (err: any) {
      console.error('Error loading user applications:', err);
      setEditFormData({
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        status: user.status,
        roleIds: user.roles.map((r) => r.id),
        applicationIds: [],
      });
    }

    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editUser) return;

    if (!editFormData.firstName || !editFormData.lastName || !editFormData.email) {
      toast.error(t('common:validation.required'));
      return;
    }

    const loadingToast = toast.loading(t('settings:users.updating') || 'Updating user...');

    try {
      await userApi.update(editUser.id, {
        firstName: editFormData.firstName,
        lastName: editFormData.lastName,
        email: editFormData.email,
        status: editFormData.status,
        roleIds: editFormData.roleIds,
      });

      // Update application assignments
      await userApi.assignApplications(editUser.id, editFormData.applicationIds);

      toast.success(t('settings:users.updateSuccess') || 'User updated successfully!', { id: loadingToast });

      setShowEditModal(false);
      setEditUser(null);
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      toast.error(err.message || t('settings:users.updateError') || 'Failed to update user', { id: loadingToast });
    }
  };

  const handleDelete = async (userId: string) => {
    if (!confirm(t('settings:users.deleteConfirm') || 'Are you sure you want to delete this user?')) return;

    const loadingToast = toast.loading(t('settings:users.deleting') || 'Deleting user...');

    try {
      await userApi.delete(userId);
      toast.success(t('settings:users.deleteSuccess') || 'User deleted successfully!', { id: loadingToast });
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      toast.error(err.message || t('settings:users.deleteError') || 'Failed to delete user', { id: loadingToast });
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">Loading users...</div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-red-800 dark:text-red-400">Error: {error}</p>
        </div>
      </MainLayout>
    );
  }

  const adminCount = users.filter(u => u.roles.some(r => r.name.toLowerCase() === 'admin')).length;
  const activeCount = users.filter(u => u.status === 'active').length;

  return (
    <MainLayout>
      <BackButton />
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('settings:users.title')}</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">{t('settings:users.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('settings:users.inviteUser')}
        </button>
      </div>

      {/* Stats */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('common:labels.total')}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{users.length}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('common:status.active')}</p>
          <p className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">
            {activeCount}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">Admins</p>
          <p className="mt-2 text-3xl font-bold text-purple-600 dark:text-purple-400">
            {adminCount}
          </p>
        </div>
      </div>

      {/* Users Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings:team.members')}</h2>
        </div>
        {users.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            No users found
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-gray-200 dark:border-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  {t('settings:users.name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  {t('settings:users.email')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  {t('settings:users.role')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  {t('settings:users.statusLabel')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  {t('settings:users.lastLogin')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  {t('settings:users.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
                        {user.first_name[0]}
                        {user.last_name[0]}
                      </div>
                      <div className="ml-4">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {user.first_name} {user.last_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{user.email}</td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {user.roles.map((role) => (
                        <span key={role.id} className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getRoleColor(role.name)}`}>
                          {role.name}
                        </span>
                      ))}
                      {user.roles.length === 0 && (
                        <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400">
                          No role
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(user.status)}`}>
                      {t(`settings:users.status.${user.status}`)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                    {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : t('settings:users.neverLoggedIn')}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex space-x-2">
                      {user.status === 'invited' && (
                        <button
                          onClick={() => handleResendInvite(user.id, user.email)}
                          className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                        >
                          {t('settings:users.resendInvite')}
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        {t('common:buttons.edit')}
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        {t('common:buttons.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md max-h-[90vh] rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-800 dark:bg-gray-900 flex flex-col">
            <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('settings:users.invite.title')}</h3>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings:users.edit.firstName')}
                </label>
                <input
                  type="text"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  placeholder="John"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings:users.edit.lastName')}
                </label>
                <input
                  type="text"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  placeholder="Doe"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings:users.invite.email')}
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('settings:users.invite.role')}
                </label>
                <div className="space-y-3 border border-gray-300 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                  {roles.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                      {t('common:loading')}...
                    </p>
                  )}
                  {roles.map((role) => (
                    <label key={role.id} className="flex items-start space-x-3 cursor-pointer hover:bg-white dark:hover:bg-gray-800 p-2 rounded-lg transition-colors">
                      <input
                        type="checkbox"
                        checked={inviteRoleIds.includes(role.id)}
                        onChange={(e) => {
                          const newIds = e.target.checked
                            ? [...inviteRoleIds, role.id]
                            : inviteRoleIds.filter((id) => id !== role.id);
                          setInviteRoleIds(newIds);
                        }}
                        className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {t(`settings:users.roleLabels.${role.name}`)}
                        </div>
                        {role.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {t(`settings:users.roleDescriptions.${role.name}`)}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              {/* Application Access - Only show if not Admin */}
              {(() => {
                const hasAdminRole = roles.some(role => inviteRoleIds.includes(role.id) && role.name === 'Admin');
                if (hasAdminRole) {
                  return (
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50 dark:border-indigo-900 dark:bg-indigo-900/20 p-4">
                      <div className="flex items-start space-x-3">
                        <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-indigo-900 dark:text-indigo-100">
                            {t('settings:users.adminAccessNote')}
                          </p>
                          <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-1">
                            {t('settings:users.adminAccessDescription')}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('settings:users.applicationAccess')}
                    </label>
                    <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-lg p-3">
                      {applications.map((app) => (
                        <label key={app.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={inviteApplicationIds.includes(app.id)}
                            onChange={(e) => {
                              const newIds = e.target.checked
                                ? [...inviteApplicationIds, app.id]
                                : inviteApplicationIds.filter((id) => id !== app.id);
                              setInviteApplicationIds(newIds);
                            }}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {app.name} <span className="text-xs text-gray-500">({app.environment})</span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {t('settings:users.applicationAccessHint')}
                    </p>
                  </div>
                );
              })()}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
              <div className="flex space-x-3">
                <button
                  onClick={handleInvite}
                  className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  {t('settings:users.invite.send')}
                </button>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  {t('common:buttons.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md max-h-[90vh] rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-800 dark:bg-gray-900 flex flex-col">
            <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('settings:users.edit.title')}</h3>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings:users.edit.firstName')}
                </label>
                <input
                  type="text"
                  value={editFormData.firstName}
                  onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings:users.edit.lastName')}
                </label>
                <input
                  type="text"
                  value={editFormData.lastName}
                  onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings:users.edit.email')}
                </label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('settings:users.role')}
                </label>
                <div className="space-y-3 border border-gray-300 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                  {roles.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                      {t('common:loading')}...
                    </p>
                  )}
                  {roles.map((role) => (
                    <label key={role.id} className="flex items-start space-x-3 cursor-pointer hover:bg-white dark:hover:bg-gray-800 p-2 rounded-lg transition-colors">
                      <input
                        type="checkbox"
                        checked={editFormData.roleIds.includes(role.id)}
                        onChange={(e) => {
                          const newIds = e.target.checked
                            ? [...editFormData.roleIds, role.id]
                            : editFormData.roleIds.filter((id) => id !== role.id);
                          setEditFormData({ ...editFormData, roleIds: newIds });
                        }}
                        className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {t(`settings:users.roleLabels.${role.name}`)}
                        </div>
                        {role.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {t(`settings:users.roleDescriptions.${role.name}`)}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              {/* Application Access - Only show if not Admin */}
              {(() => {
                const hasAdminRole = roles.some(role => editFormData.roleIds.includes(role.id) && role.name === 'Admin');
                if (hasAdminRole) {
                  return (
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50 dark:border-indigo-900 dark:bg-indigo-900/20 p-4">
                      <div className="flex items-start space-x-3">
                        <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-indigo-900 dark:text-indigo-100">
                            {t('settings:users.adminAccessNote')}
                          </p>
                          <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-1">
                            {t('settings:users.adminAccessDescription')}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('settings:users.applicationAccess')}
                    </label>
                    <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-lg p-3">
                      {applications.map((app) => (
                        <label key={app.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editFormData.applicationIds.includes(app.id)}
                            onChange={(e) => {
                              const newIds = e.target.checked
                                ? [...editFormData.applicationIds, app.id]
                                : editFormData.applicationIds.filter((id) => id !== app.id);
                              setEditFormData({ ...editFormData, applicationIds: newIds });
                            }}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {app.name} <span className="text-xs text-gray-500">({app.environment})</span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {t('settings:users.applicationAccessHint')}
                    </p>
                  </div>
                );
              })()}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings:users.edit.status')}
                </label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as 'active' | 'inactive' | 'suspended' })}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                >
                  <option value="active">{t('settings:users.status.active')}</option>
                  <option value="inactive">{t('settings:users.status.inactive')}</option>
                  <option value="suspended">{t('settings:users.status.suspended')}</option>
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
              <div className="flex space-x-3">
                <button
                  onClick={handleUpdate}
                  className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  {t('settings:users.edit.save')}
                </button>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditUser(null);
                  }}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  {t('common:buttons.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
